import $ from 'jquery';

import Keyboard from 'app/Keyboard';
import Format from 'app/src/data/Format';
import BaseLayoutView from 'app/src/views/base/BaseLayoutView';

import paymentDefaultTemplate from './templates/payment-default.hbs';
import paymentRefundDefaultTemplate from './templates/payment-refund-default.hbs';

// events triggered by a PaymentDefaultView
let _Events = {
  DENOMINATION_CLICKED: 'denomination:clicked',
  PAYMENT_CHANGED: 'payment:changed',
  MAX_BUTTON_CLICKED: 'max:button:clicked',
  CREDIT_DEBIT_CLICKED: 'credit:debit:clicked',
  SAVED_CARDS_CLICKED: 'saved:cards:clicked',
  GIFT_CARD_BUTTON_CLICKED: 'gift:card:button:clicked',
  FINISH_SALE_REQUESTED: 'finish:sale:requested',
};

// configure
let View = BaseLayoutView.extend({
  template: paymentDefaultTemplate,

  ui: {
    denomination: 'button.denomination',
    change: '#cash_remainder',
    changeBalance: '#cash_remainder_balance',
    maxButton: 'button[id$="MaxButton"]',
    paymentInput: 'input[id$="_payment"]',
    cashPaymentInput: '#cash_payment',
    creditDebit: '#credit_card_button',
    savedCardsButton: '#saved_cards_button',
    giftCardButton: '#gift_card_button',
    creditAvailableAmount: '#creditAvailable var',
    depositPaymentButton: '#DepositPaymentEditButton',
    depositPaymentInput: '#deposit_payment',
    cashRoundedCashMax: '#cashRoundedCashMax',
    cashRoundingInfo: '#cashRoundingInfo',
    cashRoundingInfoLabel: '#cashRoundingInfoLabel',
  },

  triggers: {
    'click @ui.creditDebit': _Events.CREDIT_DEBIT_CLICKED,
    'click @ui.giftCardButton': _Events.GIFT_CARD_BUTTON_CLICKED,
  },

  events: {
    'keypress @ui.paymentInput': 'onPaymentKeypress',
    'click @ui.denomination': 'onDenominationClicked',
    'click @ui.maxButton': 'onMaxButtonClicked',
    'change @ui.paymentInput': 'onPaymentChanged',
    'click @ui.depositPaymentButton': 'onDepositPaymentButtonClicked',
    'click @ui.savedCardsButton': 'onSavedCardsButtonClicked',
  },

  regions: {
    paymentList: '#special_payments_list',
  },
});

// define
export default class PaymentDefaultView extends View {
  static get Event() {
    return _Events;
  }

  getTemplate() {
    if (this.viewData.saleData.isRefundSale) {
      return paymentRefundDefaultTemplate;
    }
    return paymentDefaultTemplate;
  }

  /**
   * Event handler called before the view is rendered.
   */
  onBeforeRender() {
    // Attach a window.Handlebars helper to break at ever 4th currency denomination
    window.Handlebars.registerHelper(
      'addBreakOn4thCurrency',
      (index) => new window.Handlebars.SafeString((index + 1) % 4 === 0 ? '<br>' : '')
    );

    window.Handlebars.registerHelper(
      'requiresButHasNoCustomer',
      (paymentType) => paymentType.requireCustomer && this.viewData.saleData.sale.customerID === 0
    );
  }

  /**
   * Event handler called before the view is destroyed.
   */
  onBeforeDestroy() {
    window.Handlebars.unregisterHelper('addBreakOn4thCurrency');
    window.Handlebars.unregisterHelper('requiresButHasNoCustomer');
    window.Handlebars.unregisterHelper('isPaymentProviderNotAvailable');
  }

  /**
   * Event handler for when a denomination button is clicked.
   *
   * @param {Event} event The triggering event.
   */
  onDenominationClicked(event) {
    this.trigger(_Events.DENOMINATION_CLICKED, $(event.target).attr('cash-value'));
  }

  /**
   * Event handler for when a max button has been clicked.
   *
   * @param {Event} event The triggering event.
   */
  onMaxButtonClicked(event) {
    let $button = $(event.target);
    let paymentTypeID = Format.parseInt($button.attr('payment_type_id'));

    if (paymentTypeID) {
      this.trigger(_Events.MAX_BUTTON_CLICKED, paymentTypeID);
      if ($button.attr('id') === 'CashMaxButton') {
        $(`input[payment_type_id="${paymentTypeID}"]`).trigger('select');
      } else {
        $(`input[payment_type_id="${paymentTypeID}"]`).trigger('focus');
      }
    }
  }

  /**
   * Event handler when a payment input field has been changed.
   *
   * @param {Event} event The triggering event.
   */
  onPaymentChanged(event) {
    let $input = $(event.target);
    let paymentTypeID = Format.parseInt($input.attr('payment_type_id'));

    if (paymentTypeID) {
      this.trigger(_Events.PAYMENT_CHANGED, paymentTypeID, $input.val());
    }
  }

  /**
   * Retrieves the current value of the cash payment field.
   *
   * @returns {Decimal}   The value of the payment field in decimal precision.
   */
  getCashPayment() {
    return Format.parseMoney(this.ui.cashPayment.val());
  }

  /**
   * Event handler for when a key is pressed with the focus on a payment input.
   * When the enter key is pressed we Finish the Sale if the total payments are sufficient
   *
   * @param {Event} event The triggering DOM event
   */
  onPaymentKeypress(event) {
    if (event.keyCode === Keyboard.KeyCode.ENTER) {
      this.trigger(_Events.FINISH_SALE_REQUESTED);
    }
  }

  /**
   * Event handler for when the edit button of the deposit payment method is clicked.
   */
  onDepositPaymentButtonClicked() {
    window.merchantos.register.ensureAppIsDefined();
    window.App.channel.vent.trigger(window.App.globalEvents.register.ADD_DEPOSIT_PAYMENT);
  }

  onSetDepositPaymentAmount(amount) {
    this.ui.depositPaymentInput.val(Format.formatMoney(amount, 0, false));
  }

  onSavedCardsButtonClicked() {
    this.trigger(_Events.SAVED_CARDS_CLICKED, this.viewData.savedCards);
  }

  /**
   * Sets the payment value to an input field associated with the paymentTypeID
   *
   * @param {Number} paymentTypeID   The paymentTypeID of the particular payment
   * @param {*} amount                The amount of the payment
   * @param {Boolean} disable        Whether or not we want the field to be disabled
   * @param {Boolean} hidden          Whether or not we want the field to be hidden
   */
  setPayment(paymentTypeID, amount, disable = false, hidden = false) {
    let $paymentInputField = this.ui.paymentInput.filter(`[payment_type_id="${paymentTypeID}"]`);

    $paymentInputField.val(Format.formatMoney(amount, 0, false));

    let $paymentInputFieldParent;
    if (disable) {
      // Disable the input field
      $paymentInputField.attr('disabled', 'disable');

      // Give it the appearance of a disabled field
      $paymentInputFieldParent = $paymentInputField.parent();
      $paymentInputFieldParent.addClass('disabled');

      // Also disable its "Max" button
      $paymentInputFieldParent.next().attr('disabled', 'disable');
    }

    $paymentInputFieldParent = $paymentInputField.closest('tr');
    $paymentInputFieldParent.toggleClass('hidden', hidden);
  }

  setSavedCards(savedCards) {
    this.viewData.savedCards = savedCards;
  }

  /**
   * Updates the change due portion of the view.  The view is hidden if the change
   * due is 0 otherwise the view is shown.
   *
   * @param {Decimal} amount      The amount to be displayed in the change due portion of the view.
   * @param {Decimal} cashBalance The amount of Cash that's being tendered.
   */
  setBalance(amount, cashBalance) {
    if (
      cashBalance &&
      amount &&
      (cashBalance.isZero() || cashBalance.isNaN() || amount.isZero() || amount.isNaN())
    ) {
      this.showCashBalance(false);
    } else if (amount && amount.isGreaterThan(0)) {
      this.showCashBalance(true);
    }

    this.ui.changeBalance.text(Format.formatMoney(amount));
  }

  /**
   * Shows or hides the gift card button
   *
   * @param {Boolean} show    When true, the button will be visible
   */
  showGiftCardButton(show) {
    this.ui.giftCardButton.toggleClass('hidden', !show);
  }

  disableGiftCardButton() {
    this.ui.giftCardButton.attr('disabled', 'disabled');
    this.ui.giftCardButton.attr(
      'title',
      'Redeeming gift card is unavailable. For more information, contact your administrator.'
    );
  }

  /**
   * Shows or hides the change
   *
   * @param {Boolean} show    When true, the change will be visible
   */
  showCashBalance(show) {
    this.ui.change.toggleClass('hidden', !show);
  }

  /**
   * Shows or hides the Credit/Debit button
   *
   * @param {Boolean} show    When true, the button will be visible
   */
  showCreditDebitButton(show) {
    this.ui.creditDebit.toggleClass('hidden', !show);
  }

  /**
   * Disables the Credit/Debit button
   */
  disableCreditDebitButton() {
    this.ui.creditDebit.attr('disabled', 'disabled');
  }

  /**
   * Shows or hides the Saved Cards button
   *
   */
  showSavedCardsButton(show) {
    this.ui.savedCardsButton.toggleClass('hidden', !show);
  }

  /**
   * Disables the Saved Cards button
   */
  disableSavedCardsButton(disable = true) {
    this.ui.savedCardsButton.attr('disabled', disable ? 'disabled' : false);

    if (!disable) {
      this.ui.savedCardsButton.removeAttr('title');
    }
  }

  /**
   * Sets the focus on the Cash field and highlights its text
   */
  setFocusOnCashField() {
    this.ui.cashPaymentInput.trigger('select');
  }

  /**
   * Sets the focus on the Cash field and highlights its text
   */
  setCreditAccountAvailableAmount(amount) {
    this.ui.creditAvailableAmount.text(Format.formatMoney(amount, null, false));
  }

  /**
   * Shows or hides the Deposit payment method
   *
   * @param {Boolean} show    When true, the button will be visible
   */
  showDepositPaymentMethod(show) {
    this.ui.depositPaymentInput.closest('tr').toggleClass('hidden', !show);
  }

  setCashRoundedCashMax(amount) {
    this.ui.cashRoundedCashMax.text(Format.formatMoney(amount));
  }

  showCashRoundingInfo(show) {
    if (show) {
      this.ui.cashRoundingInfo.show();
    } else {
      this.ui.cashRoundingInfo.hide();
    }
  }

  setCashRoundingInfoLabel(label) {
    this.ui.cashRoundingInfoLabel.text(label);
  }
}
